import { lazy, Suspense } from 'react';
import { RouteObject } from 'react-router-dom';
import { RouteLoadingFallback } from './components/UI';
import { GeneralErrorPage, AdminErrorPage, NotFoundPage } from './components/ErrorBoundary';

const AdminPage = lazy(() => import('./pages/admin').then(m => ({ default: m.AdminPage })));
const CustomerLogin = lazy(() => import('./pages/customer').then(m => ({ default: m.CustomerLogin })));
const CustomerVerify = lazy(() => import('./pages/customer').then(m => ({ default: m.CustomerVerify })));
const CustomerVerifyEmail = lazy(() => import('./pages/customer').then(m => ({ default: m.CustomerVerifyEmail })));
const CustomerDashboard = lazy(() => import('./pages/customer').then(m => ({ default: m.CustomerDashboard })));
const MainApp = lazy(() => import('./App'));

const MaritimeCommunications = lazy(() => import('./pages/landing').then(m => ({ default: m.MaritimeCommunications })));
const AviationSafety = lazy(() => import('./pages/landing').then(m => ({ default: m.AviationSafety })));
const RemoteExpedition = lazy(() => import('./pages/landing').then(m => ({ default: m.RemoteExpedition })));
const MarineResearch = lazy(() => import('./pages/landing').then(m => ({ default: m.MarineResearch })));
const GlobalFleetManagement = lazy(() => import('./pages/landing').then(m => ({ default: m.GlobalFleetManagement })));
const EmergencyResponse = lazy(() => import('./pages/landing').then(m => ({ default: m.EmergencyResponse })));
const OutdoorAdventure = lazy(() => import('./pages/landing').then(m => ({ default: m.OutdoorAdventure })));
const ScientificResearch = lazy(() => import('./pages/landing').then(m => ({ default: m.ScientificResearch })));
const GovernmentOperations = lazy(() => import('./pages/landing').then(m => ({ default: m.GovernmentOperations })));
const MiningOperations = lazy(() => import('./pages/landing').then(m => ({ default: m.MiningOperations })));
const OilGas = lazy(() => import('./pages/landing').then(m => ({ default: m.OilGas })));
const SimCardRefills = lazy(() => import('./pages/landing').then(m => ({ default: m.SimCardRefills })));
const RemoteWorkers = lazy(() => import('./pages/landing').then(m => ({ default: m.RemoteWorkers })));
const CanadaAlaska = lazy(() => import('./pages/landing').then(m => ({ default: m.CanadaAlaska })));
const LatinAmerica = lazy(() => import('./pages/landing').then(m => ({ default: m.LatinAmerica })));
const MiddleEastAfrica = lazy(() => import('./pages/landing').then(m => ({ default: m.MiddleEastAfrica })));
const IridiumGo = lazy(() => import('./pages/landing').then(m => ({ default: m.IridiumGo })));
const IridiumExtreme = lazy(() => import('./pages/landing').then(m => ({ default: m.IridiumExtreme })));
const GlobalCoverage = lazy(() => import('./pages/landing').then(m => ({ default: m.GlobalCoverage })));
const CartRecovery = lazy(() => import('./pages/CartRecovery'));

const withSuspense = (Component: React.LazyExoticComponent<any>) => (
  <Suspense fallback={<RouteLoadingFallback />}>
    <Component />
  </Suspense>
);

export const routes: RouteObject[] = [
  {
    path: '/admin',
    element: withSuspense(AdminPage),
    errorElement: <AdminErrorPage />,
  },
  {
    path: '/customer/login',
    element: withSuspense(CustomerLogin),
    errorElement: <GeneralErrorPage />,
  },
  {
    path: '/customer/verify',
    element: withSuspense(CustomerVerify),
    errorElement: <GeneralErrorPage />,
  },
  {
    path: '/customer/verify-email',
    element: withSuspense(CustomerVerifyEmail),
    errorElement: <GeneralErrorPage />,
  },
  {
    path: '/customer/dashboard',
    element: withSuspense(CustomerDashboard),
    errorElement: <GeneralErrorPage />,
  },
  {
    path: '/customer',
    element: withSuspense(CustomerDashboard),
    errorElement: <GeneralErrorPage />,
  },
  {
    path: '/checkout/recover',
    element: withSuspense(CartRecovery),
    errorElement: <GeneralErrorPage />,
  },
  {
    path: '/maritime-communications',
    element: withSuspense(MaritimeCommunications),
    errorElement: <GeneralErrorPage />,
  },
  {
    path: '/aviation-safety',
    element: withSuspense(AviationSafety),
    errorElement: <GeneralErrorPage />,
  },
  {
    path: '/remote-expedition',
    element: withSuspense(RemoteExpedition),
    errorElement: <GeneralErrorPage />,
  },
  {
    path: '/marine-research',
    element: withSuspense(MarineResearch),
    errorElement: <GeneralErrorPage />,
  },
  {
    path: '/global-fleet-management',
    element: withSuspense(GlobalFleetManagement),
    errorElement: <GeneralErrorPage />,
  },
  {
    path: '/emergency-response',
    element: withSuspense(EmergencyResponse),
    errorElement: <GeneralErrorPage />,
  },
  {
    path: '/outdoor-adventure',
    element: withSuspense(OutdoorAdventure),
    errorElement: <GeneralErrorPage />,
  },
  {
    path: '/scientific-research',
    element: withSuspense(ScientificResearch),
    errorElement: <GeneralErrorPage />,
  },
  {
    path: '/government-operations',
    element: withSuspense(GovernmentOperations),
    errorElement: <GeneralErrorPage />,
  },
  {
    path: '/mining-operations',
    element: withSuspense(MiningOperations),
    errorElement: <GeneralErrorPage />,
  },
  {
    path: '/oil-gas',
    element: withSuspense(OilGas),
    errorElement: <GeneralErrorPage />,
  },
  {
    path: '/sim-card-refills',
    element: withSuspense(SimCardRefills),
    errorElement: <GeneralErrorPage />,
  },
  {
    path: '/remote-workers',
    element: withSuspense(RemoteWorkers),
    errorElement: <GeneralErrorPage />,
  },
  {
    path: '/canada-alaska',
    element: withSuspense(CanadaAlaska),
    errorElement: <GeneralErrorPage />,
  },
  {
    path: '/latin-america',
    element: withSuspense(LatinAmerica),
    errorElement: <GeneralErrorPage />,
  },
  {
    path: '/middle-east-africa',
    element: withSuspense(MiddleEastAfrica),
    errorElement: <GeneralErrorPage />,
  },
  {
    path: '/iridium-go',
    element: withSuspense(IridiumGo),
    errorElement: <GeneralErrorPage />,
  },
  {
    path: '/iridium-extreme-9575',
    element: withSuspense(IridiumExtreme),
    errorElement: <GeneralErrorPage />,
  },
  {
    path: '/global-coverage',
    element: withSuspense(GlobalCoverage),
    errorElement: <GeneralErrorPage />,
  },
  {
    path: '/',
    element: withSuspense(MainApp),
    errorElement: <GeneralErrorPage />,
  },
  {
    path: '*',
    element: <NotFoundPage />,
  },
];
